/*
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, you can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * Copyright Oxide Computer Company
 */
import type * as PP from './path-params.ts'

// these are used as the basis for many routes but are not themselves routes we
// ever want to link to. so we use this to build the routes but pb.project() is
// different (includes /instances)
const projectBase = ({ project }: PP.Project) => `${pb.projects()}/${project}`
const instanceBase = ({ project, instance }: PP.Instance) =>
  `${pb.instances({ project })}/${instance}`
const vpcBase = ({ project, vpc }: PP.Vpc) => `${pb.vpcs({ project })}/${vpc}`

export const instanceMetricsBase = ({ project, instance }: PP.Instance) =>
  `${instanceBase({ project, instance })}/metrics`
export const inventoryBase = () => '/system/inventory'
export const alertingBase = () => '/system/alerting'
const siloBase = ({ silo }: PP.Silo) => `/system/silos/${silo}`

export const pb = {
  projects: () => `/projects`,
  projectsNew: () => `/projects-new`,
  project: (params: PP.Project) => `${projectBase(params)}/instances`,
  projectEdit: (params: PP.Project) => `${projectBase(params)}/edit`,

  projectAccess: (params: PP.Project) => `${projectBase(params)}/access`,
  projectImages: (params: PP.Project) => `${projectBase(params)}/images`,
  projectImagesNew: (params: PP.Project) => `${projectBase(params)}/images-new`,
  projectImage: (params: PP.Image) => `${pb.projectImages(params)}/${params.image}`,

  instances: (params: PP.Project) => `${projectBase(params)}/instances`,
  instancesNew: (params: PP.Project) => `${projectBase(params)}/instances-new`,

  /**
   * This route exists as a direct link to the default tab of the instance page. Unfortunately
   * we don't currently have a good mechanism at the moment to handle a redirect to the default
   * tab in a seemless way so we need all in-app links to go directly to the default tab.
   *
   * @see https://github.com/oxidecomputer/console/pull/1267#discussion_r1016766205
   */
  instance: (params: PP.Instance) => pb.instanceStorage(params),

  instanceCpuMetrics: (params: PP.Instance) => `${instanceMetricsBase(params)}/cpu`,
  instanceDiskMetrics: (params: PP.Instance) => `${instanceMetricsBase(params)}/disk`,
  instanceNetworkMetrics: (params: PP.Instance) => `${instanceMetricsBase(params)}/network`,
  instanceStorage: (params: PP.Instance) => `${instanceBase(params)}/storage`,
  instanceConnect: (params: PP.Instance) => `${instanceBase(params)}/connect`,
  instanceNetworking: (params: PP.Instance) => `${instanceBase(params)}/networking`,
  serialConsole: (params: PP.Instance) => `${instanceBase(params)}/serial-console`,
  instanceSettings: (params: PP.Instance) => `${instanceBase(params)}/settings`,

  disksNew: (params: PP.Project) => `${projectBase(params)}/disks-new`,
  disks: (params: PP.Project) => `${projectBase(params)}/disks`,
  disk: (params: PP.Disk) => `${pb.disks(params)}/${params.disk}`,

  snapshotsNew: (params: PP.Project) => `${projectBase(params)}/snapshots-new`,
  snapshots: (params: PP.Project) => `${projectBase(params)}/snapshots`,
  snapshotImagesNew: (params: PP.Snapshot) =>
    `${projectBase(params)}/snapshots/${params.snapshot}/images-new`,

  vpcsNew: (params: PP.Project) => `${projectBase(params)}/vpcs-new`,
  vpcs: (params: PP.Project) => `${projectBase(params)}/vpcs`,

  // same deal as instance detail: go straight to first tab
  vpc: (params: PP.Vpc) => pb.vpcFirewallRules(params),
  vpcEdit: (params: PP.Vpc) => `${vpcBase(params)}/edit`,

  vpcFirewallRules: (params: PP.Vpc) => `${vpcBase(params)}/firewall-rules`,
  vpcFirewallRulesNew: (params: PP.Vpc) => `${vpcBase(params)}/firewall-rules-new`,
  vpcFirewallRuleClone: (params: PP.FirewallRule) =>
    `${pb.vpcFirewallRulesNew(params)}/${params.rule}`,
  vpcFirewallRuleEdit: (params: PP.FirewallRule) =>
    `${pb.vpcFirewallRules(params)}/${params.rule}/edit`,
  vpcRouters: (params: PP.Vpc) => `${vpcBase(params)}/routers`,
  vpcRoutersNew: (params: PP.Vpc) => `${vpcBase(params)}/routers-new`,
  vpcRouter: (params: PP.VpcRouter) => `${pb.vpcRouters(params)}/${params.router}`,
  vpcRouterEdit: (params: PP.VpcRouter) => `${pb.vpcRouter(params)}/edit`,
  vpcRouterRouteEdit: (params: PP.VpcRouterRoute) =>
    `${pb.vpcRouter(params)}/routes/${params.route}/edit`,
  vpcRouterRoutesNew: (params: PP.VpcRouter) => `${pb.vpcRouter(params)}/routes-new`,

  vpcSubnets: (params: PP.Vpc) => `${vpcBase(params)}/subnets`,
  vpcSubnetsNew: (params: PP.Vpc) => `${vpcBase(params)}/subnets-new`,
  vpcSubnetsEdit: (params: PP.VpcSubnet) =>
    `${pb.vpcSubnets(params)}/${params.subnet}/edit`,

  vpcInternetGateways: (params: PP.Vpc) => `${vpcBase(params)}/internet-gateways`,
  vpcInternetGateway: (params: PP.VpcInternetGateway) =>
    `${pb.vpcInternetGateways(params)}/${params.gateway}`,
  // vpcInternetGatewaysNew: (params: Vpc) => `${vpcBase(params)}/internet-gateways-new`,
  //
  externalSubnets: (params: PP.Project) => `${projectBase(params)}/external-subnets`,
  externalSubnetsNew: (params: PP.Project) => `${projectBase(params)}/external-subnets-new`,
  externalSubnetEdit: (params: PP.ExternalSubnet) =>
    `${pb.externalSubnets(params)}/${params.externalSubnet}/edit`,

  floatingIps: (params: PP.Project) => `${projectBase(params)}/floating-ips`,
  floatingIpsNew: (params: PP.Project) => `${projectBase(params)}/floating-ips-new`,
  floatingIpEdit: (params: PP.FloatingIp) =>
    `${pb.floatingIps(params)}/${params.floatingIp}/edit`,

  affinity: (params: PP.Project) => `${projectBase(params)}/affinity`,
  affinityNew: (params: PP.Project) => `${projectBase(params)}/affinity-new`,
  antiAffinityGroup: (params: PP.AntiAffinityGroup) =>
    `${pb.affinity(params)}/${params.antiAffinityGroup}`,
  antiAffinityGroupEdit: (params: PP.AntiAffinityGroup) =>
    `${pb.antiAffinityGroup(params)}/edit`,

  siloUtilization: () => '/utilization',
  siloAccess: () => '/access',
  siloImages: () => '/images',
  siloImage: (params: PP.SiloImage) => `${pb.siloImages()}/${params.image}`,

  fleetAccess: () => '/system/access',
  systemMetricsExplorer: () => '/system/metrics-explorer',
  systemUtilization: () => '/system/utilization',

  ipPools: () => '/system/networking/ip-pools',
  ipPoolsNew: () => '/system/networking/ip-pools-new',
  ipPool: (params: PP.IpPool) => `${pb.ipPools()}/${params.pool}`,
  ipPoolEdit: (params: PP.IpPool) => `${pb.ipPool(params)}/edit`,
  ipPoolRangeAdd: (params: PP.IpPool) => `${pb.ipPool(params)}/ranges-add`,

  subnetPools: () => '/system/networking/subnet-pools',
  subnetPoolsNew: () => '/system/networking/subnet-pools-new',
  subnetPool: (params: PP.SubnetPool) => `${pb.subnetPools()}/${params.subnetPool}`,
  subnetPoolEdit: (params: PP.SubnetPool) => `${pb.subnetPool(params)}/edit`,
  subnetPoolMemberAdd: (params: PP.SubnetPool) => `${pb.subnetPool(params)}/members-add`,

  alerts: () => `${alertingBase()}/alerts`,
  alertReceivers: () => `${alertingBase()}/receivers`,
  alertReceiversNew: () => `${alertingBase()}/receivers-new`,
  alertReceiver: (params: PP.AlertReceiver) => `${pb.alertReceivers()}/${params.receiver}`,
  alertReceiverEdit: (params: PP.AlertReceiver) => `${pb.alertReceiver(params)}/edit`,

  sledInventory: () => `${inventoryBase()}/sleds`,
  diskInventory: () => `${inventoryBase()}/disks`,
  sledInstances: ({ sledId }: PP.Sled) => `${pb.sledInventory()}/${sledId}/instances`,

  silos: () => '/system/silos',
  silosNew: () => '/system/silos-new',
  // canonical route for silo is first tab
  silo: (params: PP.Silo) => pb.siloIdps(params),
  siloIdps: (params: PP.Silo) => `${siloBase(params)}/idps`,
  siloIdpsNew: (params: PP.Silo) => `${siloBase(params)}/idps-new`,
  siloIpPools: (params: PP.Silo) => `${siloBase(params)}/ip-pools`,
  siloSubnetPools: (params: PP.Silo) => `${siloBase(params)}/subnet-pools`,
  siloQuotas: (params: PP.Silo) => `${siloBase(params)}/quotas`,
  siloFleetRoles: (params: PP.Silo) => `${siloBase(params)}/fleet-roles`,
  siloScim: (params: PP.Silo) => `${siloBase(params)}/scim`,
  samlIdp: (params: PP.IdentityProvider) =>
    `${siloBase(params)}/idps/saml/${params.provider}`,

  systemUpdate: () => '/system/update',

  supportBundles: () => '/system/support-bundles',
  supportBundlesNew: () => '/system/support-bundles-new',
  supportBundle: (params: PP.SupportBundle) => `${pb.supportBundles()}/${params.bundleId}`,
  auditLog: () => '/system/audit-log',

  profile: () => '/settings/profile',
  sshKeys: () => '/settings/ssh-keys',
  sshKeysNew: () => '/settings/ssh-keys-new',
  sshKeyEdit: (params: PP.SshKey) => `/settings/ssh-keys/${params.sshKey}/edit`,
  accessTokens: () => '/settings/access-tokens',

  deviceSuccess: () => '/device/success',
}

// export const jelly = 'just kidding'
